import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { db } from '../../../../firebase';
import { collection, query, where, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

// Event Attendance interface
interface EventAttendance {
  eventId?: string;   // For system events, this links to the event in the database
  importedEventId?: string;  // Generated ID for imported events
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  ticketType?: string;
  status?: string;
  attendeeId?: string;
  importDate: string;
  notes?: string;
}

// Contact interface
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
  notes?: string;
  engagementScore: number;
  lastContactDate?: Date;
  source: string;
  createdAt: Date;
  consentStatus: 'granted' | 'denied' | 'unknown';
  eventName?: string;
  eventDate?: string;
  eventDescription?: string;
  importDate?: string;
  eventAttendance?: EventAttendance[];
}

// TabPanel interface and component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-view-tabpanel-${index}`}
      aria-labelledby={`contact-view-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ContactViewDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
  organizationId: string;
}

const ContactViewDialog: React.FC<ContactViewDialogProps> = ({
  open,
  onClose,
  contactId,
  organizationId
}) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<EventAttendance[]>([]);

  // Fetch contact data when dialog opens
  useEffect(() => {
    if (open && contactId && organizationId) {
      fetchContactData();
    }
  }, [open, contactId, organizationId]);

  // Process events and assign unique IDs to imported events if needed
  const processEvents = async (contactData: Contact, eventAttendance: EventAttendance[]) => {
    let eventsUpdated = false;
    
    // Process existing events - ensure each has a unique ID
    const processedEvents = eventAttendance.map(event => {
      // For imported events without an internal eventId, ensure they have an importedEventId
      if (!event.eventId && !event.importedEventId) {
        eventsUpdated = true;
        return { ...event, importedEventId: uuidv4() };
      }
      return event;
    });
    
    // If no events but we have legacy event data, create one
    if (processedEvents.length === 0 && contactData.eventName) {
      eventsUpdated = true;
      processedEvents.push({
        eventName: contactData.eventName,
        eventDate: contactData.eventDate || '',
        eventDescription: contactData.eventDescription || '',
        importDate: contactData.importDate || (contactData.createdAt ? new Date(contactData.createdAt.getTime()).toISOString().split('T')[0] : ''),
        notes: '',
        importedEventId: uuidv4()
      });
    }
    
    // If we had to update events, save back to Firestore
    if (eventsUpdated) {
      try {
        const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
        await updateDoc(contactRef, {
          eventAttendance: processedEvents
        });

      } catch (err) {
        console.error('Error updating contact events:', err);
      }
    }
    
    return processedEvents;
  };

  const fetchContactData = async () => {
    if (!contactId || !organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch contact document
      const contactRef = doc(db, `organizations/${organizationId}/contacts/${contactId}`);
      const contactSnap = await getDoc(contactRef);
      
      if (contactSnap.exists()) {
        const data = contactSnap.data();
        const contactData: Contact = {
          id: contactSnap.id,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          company: data.company || '',
          title: data.title || '',
          tags: Array.isArray(data.tags) ? data.tags : [],
          notes: data.notes || '',
          engagementScore: typeof data.engagementScore === 'number' ? data.engagementScore : 0,
          lastContactDate: data.lastContactDate ? new Date(data.lastContactDate.seconds * 1000) : undefined,
          source: data.source || 'manual',
          createdAt: data.createdAt ? new Date(data.createdAt.seconds * 1000) : new Date(),
          consentStatus: data.consentStatus || 'unknown',
          eventName: data.eventName || '',
          eventDate: data.eventDate || '',
          eventDescription: data.eventDescription || '',
          importDate: data.importDate || '',
          eventAttendance: data.eventAttendance || []
        };
        
        setContact(contactData);
        
        // Process and update events with unique IDs
        const processedEvents = await processEvents(
          contactData, 
          Array.isArray(data.eventAttendance) ? data.eventAttendance : []
        );
        
        setEvents(processedEvents);
      } else {
        setError('Contact not found');
      }
    } catch (err) {
      console.error('Error fetching contact:', err);
      setError('Error loading contact data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Format date in a friendly way
  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return '—';
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '—';
      
      // Get today's date at midnight for proper day comparison
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Convert date to same format (midnight) for comparison
      const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      // For recent dates, show relative time
      const diffTime = Math.abs(today.getTime() - compareDate.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      // Convert to Eastern Time
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      };
      
      const fullDateTime = date.toLocaleString('en-US', options) + ' EST';
      
      if (diffDays === 0) {
        // Today
        return `Today at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      } else if (diffDays === 1) {
        // Yesterday
        return `Yesterday at ${date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/New_York'
        })} EST`;
      }
      
      // Full date and time for older dates
      return fullDateTime;
    } catch (e) {
      return '—';
    }
  };

  // Get full name
  const getFullName = () => {
    if (!contact) return '';
    return `${contact.firstName} ${contact.lastName}`.trim();
  };

  // Check if this is a system contact (attended an event created in the system)
  const isSystemContact = () => {
    if (!contact) return false;
    return events.some(event => event.importedEventId); // If any event has an importedEventId, it's a system event
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Contact Details</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : contact ? (
          <>
            {/* Contact header with basic info */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                {contact.firstName[0]}{contact.lastName ? contact.lastName[0] : ''}
              </Avatar>
              <Box>
                <Typography variant="h5">{getFullName()}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {contact.title}{contact.title && contact.company ? ' at ' : ''}{contact.company}
                </Typography>
                {isSystemContact() && (
                  <Chip 
                    label="System Contact" 
                    color="primary" 
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
            </Box>

            {/* Tabs for different sections */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="contact view tabs">
                <Tab label="Profile" id="contact-view-tab-0" aria-controls="contact-view-tabpanel-0" />
                <Tab label="Events" id="contact-view-tab-1" aria-controls="contact-view-tabpanel-1" />
                <Tab label="Metrics" id="contact-view-tab-2" aria-controls="contact-view-tabpanel-2" />
              </Tabs>
            </Box>

            {/* Profile Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Contact Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography>{contact.email}</Typography>
                    </Box>
                    {contact.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>{contact.phone}</Typography>
                      </Box>
                    )}
                    {contact.company && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography>{contact.company}</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Additional Information
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Source</Typography>
                      <Chip 
                        label={contact.source === 'ai_import' ? 'AI Import' : contact.source} 
                        size="small"
                        color={contact.source === 'ai_import' ? 'secondary' : 'default'}
                        sx={{ mt: 0.5, textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Created</Typography>
                      <Typography>{formatDate(contact.createdAt)}</Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Last Contact</Typography>
                      <Typography>{contact.lastContactDate ? formatDate(contact.lastContactDate) : 'Never'}</Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Consent Status</Typography>
                      <Chip 
                        label={contact.consentStatus} 
                        size="small"
                        color={contact.consentStatus === 'granted' ? 'success' : 
                              contact.consentStatus === 'denied' ? 'error' : 'default'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {contact.tags.length > 0 ? (
                        contact.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No tags</Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                {contact.notes && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        Notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {contact.notes}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Events Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Event History
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  This contact has attended {events.length} event{events.length !== 1 ? 's' : ''}
                  {events.filter(event => !event.importedEventId).length > 0 ? 
                    ` (${events.filter(event => !event.importedEventId).length} imported, ${events.filter(event => event.importedEventId).length} internal)` : 
                    ''}
                </Typography>
              </Box>
              
              {/* If we have imported events, show a section for them */}
              {events.filter(event => !event.importedEventId).length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: '50%', 
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ccc',
                      mr: 1 
                    }}/>
                    Imported Events
                  </Typography>
                  <Paper variant="outlined">
                    <TableContainer>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell>Event Name</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {events.filter(event => !event.importedEventId).map((event, index) => (
                            <TableRow key={event.eventId}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <EventIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="body2">{event.eventName}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatDate(event.eventDate)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                  {event.eventDescription || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={event.status || "Attended"} 
                                  size="small"
                                  color={
                                    event.status === 'registered' ? 'info' : 
                                    event.status === 'cancelled' ? 'error' :
                                    event.status === 'attended' ? 'success' : 'default'
                                  }
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}
              
              {/* If we have internal events, show a section for them */}
              {events.filter(event => event.importedEventId).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: '50%', 
                      backgroundColor: theme => theme.palette.primary.main,
                      mr: 1 
                    }}/>
                    Internal Events
                  </Typography>
                  <Paper variant="outlined">
                    <TableContainer>
                      <Table size="medium">
                        <TableHead>
                          <TableRow>
                            <TableCell>Event Name</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {events.filter(event => event.importedEventId).map((event, index) => (
                            <TableRow key={event.importedEventId}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                                  <Typography variant="body2">{event.eventName}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatDate(event.eventDate)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                  {event.eventDescription || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={event.status || "Attended"} 
                                  size="small"
                                  color={
                                    event.status === 'registered' ? 'info' : 
                                    event.status === 'cancelled' ? 'error' :
                                    event.status === 'attended' ? 'success' : 'default'
                                  }
                                  sx={{ textTransform: 'capitalize' }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}
              
              {/* If no events at all, show a message */}
              {events.length === 0 && (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    This contact has not attended any events yet.
                  </Typography>
                </Paper>
              )}
              
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Legend
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: '#f0f0f0',
                      border: '1px solid #ccc',
                      mr: 1 
                    }}/>
                    <Typography variant="body2" color="text.secondary">
                      Imported Events - From external sources
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: theme => theme.palette.primary.main,
                      mr: 1 
                    }}/>
                    <Typography variant="body2" color="text.secondary">
                      Internal Events - Created in this system
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            {/* Metrics Tab */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Engagement
                    </Typography>
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Engagement Score</Typography>
                        <Typography variant="body2" fontWeight="bold">{contact.engagementScore}%</Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={contact.engagementScore} 
                        sx={{ height: 8, borderRadius: 5 }}
                      />
                    </Box>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        This score represents the level of engagement based on interactions, 
                        event attendance, email opens, and other activities.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Activity Summary
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Total Events Attended</Typography>
                      <Typography variant="h6">{events.length}</Typography>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">Event Breakdown</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: '#f0f0f0',
                            border: '1px solid #ccc',
                            mr: 1 
                          }}/>
                          <Typography variant="body2">Imported Events</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          {events.filter(event => !event.importedEventId).length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            backgroundColor: 'primary.main', 
                            mr: 1 
                          }}/>
                          <Typography variant="body2">Internal Events</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                          {events.filter(event => event.importedEventId).length}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        ) : (
          <Typography>Contact not found</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            // Here you could add logic to open contact in edit mode
            onClose();
          }}
        >
          Edit Contact
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactViewDialog;
